import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment-timezone'
import {
  Row,
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { useHistory } from 'react-router'
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined'
import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined'
import AddIcon from '@material-ui/icons/Add'
import Grid from '@material-ui/core/Grid'
import SyncIcon from '@material-ui/icons/Sync'

import { TableMenuButton } from 'civic-champs-shared/core/TableMenuButton'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { ContainedButton } from 'civic-champs-shared/core'

import { useConditionalSelectColumn } from 'core/table/table-hooks/useSelectColumn'
import useGetColumnState from 'core/table/table-hooks/useGetColumnState'
import { useFiltersFromQuery } from 'core/table/components/NewFiltersModal'
import { ExtendedPagedTable } from 'core/table/components/ExtendedPagedTable'
import useActivityLogColumns, { useStyles as useColumnStyles } from '../hooks/useActivityLogColumns'
import { useDateRangeFilter } from 'filtering/hooks'
import Loading from 'components/Loading'
import ExportActivityLogButton from '../components/ExportActivityLogButton'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'

import DEFAULT_FILTERS, { useGetGlobalFilter } from 'core/table/filters'
import CUSTOM_FILTERS from '../utils/filters'

import {
  ARRAY_OPERATOR_OPTIONS,
  NUMBER_OPERATOR_OPTIONS,
  operatorTypes,
  STRING_OPERATOR_OPTIONS,
} from 'core/table/interfaces/Filters'
import requestWithRetry from 'civic-champs-shared/api/requestWithRetry'
import { useUISettings } from 'hooks/useUISettings'
import { useActivitiesCollection } from '../hooks/useActivitiesCollection'
import { Activity } from 'champion/utils/interface'
import { useFeatureEnabled } from 'core/feature/hooks'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { ActivityImportDialog } from 'tracking/activity/components/ActivityImportDialog'
import { useHasRole } from 'auth/hooks'
import { INTERNAL_SUPER_ADMIN, SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import usePushActivitiesDialog from 'tracking/activity/hooks/usePushActivitiesDialog'
import { IntegrationKey, IntegrationProvider } from 'integrations/types'
import TablePopoverButton from 'civic-champs-shared/core/TablePopoverButton'
import { makeStyles } from '@material-ui/core/styles'
import { Opportunity } from '../../../Event/interfaces'
import useGetIntegrationKey, { emptyValue } from '../../../integrations/hooks/useGetIntegrationKey'
import useMessageRecipientsPrompt from 'messages/hooks/useMessageRecipientsPrompt'
import { useGetSearchableContactsFromVolunteersArray } from 'messages/hooks/useGetSearchableContactsFromVolunteersArray'
import { useAddRemoveTagsPrompt } from 'champion/hooks/useAddRemoveTagsPrompt'
import useMultiGroupAddMembersPrompt from 'group/hooks/useMultiGroupAddMembersPrompt'
import { Person } from 'people/interface'

const useStyles = makeStyles({
  button: {
    width: '210px',
  },
})

const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'firstName':
    case 'lastName':
    case 'opportunity':
    case 'name':
      return STRING_OPERATOR_OPTIONS

    case 'hours':
      return NUMBER_OPERATOR_OPTIONS

    case 'role':
      return [
        { value: operatorTypes.ANY_OF, displayValue: 'Include any of' },
        { value: operatorTypes.NOT_ANY_OF, displayValue: 'Do not include any of' },
      ]

    case 'reflections':
      return [{ value: operatorTypes.CONTAINS, displayValue: 'Contains' }]

    case 'satisfaction':
      return [
        { value: operatorTypes.ANY_OF, displayValue: 'Include any of' },
        { value: operatorTypes.NOT_ANY_OF, displayValue: 'Do not include any of' },
      ]

    case 'groups':
    case 'volunteerTags':
      return ARRAY_OPERATOR_OPTIONS

    default:
      return []
  }
}

const exportAllHandler = async () => {
  const blob = await requestWithRetry({
    url: '/activity_export',
    config: {
      responseType: 'blob',
    },
  })
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.setAttribute('download', 'activity.csv')
  link.click()
}

interface Props {
  tableName: string
  opportunity?: Opportunity
  locationId?: number
  locationRoleIds?: number[]
  occurrenceId?: number
  fixedDateRange?: {
    startDate: moment.Moment
    endDate: moment.Moment
  }
  hideDateRange?: boolean
  getActivitySummary?: (activities: Row<Activity>[]) => {
    count: number
    averageHours: number
    totalHours: number
  }
  cards?: {
    header: string
    accessor: string
    transform: (value: any) => any
  }[]
}

export const ActivityLog = React.memo((props: Props) => {
  const {
    tableName,
    opportunity,
    getActivitySummary,
    locationId,
    locationRoleIds,
    occurrenceId,
    fixedDateRange,
    hideDateRange,
    cards,
  } = props

  const history = useHistory()
  // @ts-ignore
  const { showAdminItems } = useUISettings()
  const skipReset = useRef<boolean>()
  const activityImportFeatureEnabled = useFeatureEnabled('ActivityImport')
  const activityImportForSuperAdminFeatureEnabled = useFeatureEnabled('ActivityImportForSuperAdmins')
  const lglIntegrationEnabled = useFeatureEnabled('LglIntegration')
  const donorPerfectIntegrationEnabled = useFeatureEnabled('DonorPerfectIntegration')
  const bulkAddMembersEnabled = useFeatureEnabled('BulkAddMembers')
  const championsTagsEnabled = useFeatureEnabled('ChampionsTags')
  const isInternalSuperAdmin = useHasRole(INTERNAL_SUPER_ADMIN)
  const isSuperAdmin = useHasRole(SUPER_ADMIN)
  const showPushActivitiesDialog = usePushActivitiesDialog({
    providerName: 'Little Green Light',
    provider: IntegrationProvider.LGL,
  })
  const showPushActivitiesToDonorPerfectDialog = usePushActivitiesDialog({
    providerName: 'DonorPerfect',
    provider: IntegrationProvider.DONOR_PERFECT,
  })
  const activityImportEnabled =
    activityImportFeatureEnabled &&
    !locationId &&
    (isInternalSuperAdmin || (isSuperAdmin && activityImportForSuperAdminFeatureEnabled))

  const classes = useStyles()
  const columnStyles = useColumnStyles()
  const [reduxDateRange] = useDateRangeFilter()
  const dateRange = fixedDateRange ?? reduxDateRange
  const { filters } = useFiltersFromQuery(history.location.search, getOperatorOptions)
  const activityFilters = useMemo(
    () => {
      const filters: Record<string, any> = { ...dateRange, mapOccurrencesApproximately: true }
      if (locationId) {
        filters.locationId = locationId
        if ((locationRoleIds?.length ?? 0) > 0) {
          filters.locationRoleIds = locationRoleIds
        }
      } else {
        filters.opportunityId = opportunity?.id
      }
      return filters
    },
    [dateRange, opportunity?.id, locationId, locationRoleIds], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const extraParams = useMemo(
    () => ({
      forLocations: !!locationId,
      locationRoleIds: (locationRoleIds?.length ?? 0) > 0 ? locationRoleIds : undefined,
    }),
    [locationId, locationRoleIds],
  )

  const [
    { activities, loading, loadingProgress },
    { addActivity, editActivity, removeActivities, refetch },
    eventListeners,
  ] = useActivitiesCollection(activityFilters, extraParams)
  const showMessageRecipientsPrompt = useMessageRecipientsPrompt()
  const getSearchableContacts = useGetSearchableContactsFromVolunteersArray(activities ?? [])
  const handleTagsChange = useCallback(async () => {
    await refetch()
  }, [refetch])
  const showTagsPrompt = useAddRemoveTagsPrompt(handleTagsChange)
  const handleGroupsAdded = useCallback(async () => {
    await refetch()
  }, [refetch])
  const showAddMembersPrompt = useMultiGroupAddMembersPrompt(handleGroupsAdded)

  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const organization = useCurrentOrg()

  const [lglIntegrationKey, setLglIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const [dpIntegrationKey, setDpIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const fetchIntegrationKey = useGetIntegrationKey()
  useEffect(() => {
    fetchIntegrationKey(IntegrationProvider.LGL).then(setLglIntegrationKey)
    fetchIntegrationKey(IntegrationProvider.DONOR_PERFECT).then(setDpIntegrationKey)
  }, [fetchIntegrationKey])

  // @ts-ignore
  const columns = useActivityLogColumns({ onEdit: editActivity }, locationId ? undefined : opportunity?.id)
  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes: { ...DEFAULT_FILTERS, ...CUSTOM_FILTERS },
    getOperatorOptions,
  })
  const showImportDialog = useShowPrompt(ActivityImportDialog)

  const filteredActivities = useMemo(() => {
    if (!activities) return []
    const { startDate, endDate } = dateRange
    const { timeZone } = organization

    if (startDate === undefined || endDate === undefined) return activities

    const filterStart = moment(startDate).tz(timeZone, true)
    const filterEnd = moment(endDate).tz(timeZone, true)

    return activities.filter((a: Activity) => {
      if (occurrenceId && a.occurrenceId && a.occurrenceId !== occurrenceId) {
        return false
      }

      const start = moment(a.occurredAt).tz(timeZone)
      const end = start.clone().add(a.hoursVolunteered, 'hours')
      return filterStart.isSameOrBefore(end, 'day') && filterEnd.isSameOrAfter(start, 'day')
    })
  }, [activities, dateRange, organization, occurrenceId])

  useEffect(() => {
    fetchColumnState(tableName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useTable(
    {
      initialState: {
        hiddenColumns: ['firstName', 'lastName', 'checkOut', 'reflections', 'groups', 'volunteerTags'],
        // @ts-ignore
        globalFilter: '',
        filters,
      },
      globalFilter,
      // @ts-ignore
      filterTypes: { ...DEFAULT_FILTERS, ...CUSTOM_FILTERS },
      data: filteredActivities,
      columns,
      autoResetSortBy: !skipReset.current,
      autoResetPage: !skipReset.current,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const [selectedRows, selectedRowsCount] = useMemo(
    () => [table.selectedFlatRows.map(({ original }: any) => original), table.selectedFlatRows.length],
    [table.selectedFlatRows],
  )

  const handleAddActivity = useCallback(() => {
    addActivity?.(opportunity)
  }, [addActivity, opportunity])

  const onRemoveActivity = async () => {
    // @ts-ignore
    await removeActivities(selectedRows)
    table.toggleAllRowsSelected(false)
  }

  const onMessageVolunteers = useCallback(() => {
    const recipientPersonIds = selectedRows.map((row: Activity) => row.user.id)
    showMessageRecipientsPrompt({
      recipientPersonIds,
      getSearchableContacts,
      autocompleteSectionTitle: 'All Volunteers',
    })
  }, [getSearchableContacts, selectedRows, showMessageRecipientsPrompt])

  const onUpdateTags = useCallback(() => {
    showTagsPrompt(selectedRows.map((row: Activity) => row.user as Person))
  }, [selectedRows, showTagsPrompt])

  const onBulkAddMembers = useCallback(async () => {
    const selectedPersons = selectedRows.map((row: Activity) => row.user as Person)
    await showAddMembersPrompt(selectedPersons)
  }, [selectedRows, showAddMembersPrompt])

  if (loading || columnStateLoading) {
    return <Loading progress={loadingProgress} animateProgress />
  }

  return (
    <Grid container>
      <ExtendedPagedTable<Activity>
        getSummary={getActivitySummary}
        cards={cards}
        history={history}
        table={table}
        columns={columns}
        columnState={columnState}
        tableName={tableName}
        searchPlaceholder="Search Activities"
        filterSubHeader="Select activity attributes to filter by:"
        buttons={
          <>
            <TableMenuButton
              startIcon={<MailOutlineIcon />}
              onClick={onMessageVolunteers}
              disabled={!selectedRowsCount}
              rightMargin
            >
              Message
            </TableMenuButton>
            <ExportActivityLogButton data={selectedRows} disabled={!selectedRowsCount} />
            <TableMenuButton
              startIcon={<RemoveCircleOutlineOutlinedIcon />}
              disabled={!selectedRowsCount}
              onClick={onRemoveActivity}
              rightMargin={showAdminItems || activityImportEnabled}
            >
              Remove
            </TableMenuButton>
            {showAdminItems && (
              <TableMenuButton
                startIcon={<SaveAltOutlinedIcon />}
                onClick={exportAllHandler}
                rightMargin={activityImportEnabled}
              >
                Export all
              </TableMenuButton>
            )}
            {activityImportEnabled && (
              <TableMenuButton startIcon={<SaveAltOutlinedIcon />} onClick={showImportDialog}>
                Import
              </TableMenuButton>
            )}
          </>
        }
        addButton={
          <ContainedButton startIcon={<AddIcon />} onClick={handleAddActivity}>
            Add Activity
          </ContainedButton>
        }
        popover={
          <>
            {bulkAddMembersEnabled && (
              <TablePopoverButton
                startIcon={<GroupOutlinedIcon />}
                disabled={!selectedRowsCount}
                onClick={onBulkAddMembers}
              >
                Add to Group
              </TablePopoverButton>
            )}
            {championsTagsEnabled && (
              <TablePopoverButton
                startIcon={<LabelOutlinedIcon />}
                disabled={!selectedRowsCount}
                onClick={onUpdateTags}
              >
                Add/Remove Tags
              </TablePopoverButton>
            )}
            {isSuperAdmin && lglIntegrationEnabled && lglIntegrationKey.enabled && !!lglIntegrationKey.token && (
              <TablePopoverButton
                className={classes.button}
                startIcon={<SyncIcon />}
                onClick={showPushActivitiesDialog}
              >
                Sync to Little Green Light
              </TablePopoverButton>
            )}
            {isSuperAdmin && donorPerfectIntegrationEnabled && dpIntegrationKey.enabled && !!dpIntegrationKey.token && (
              <TablePopoverButton
                className={classes.button}
                startIcon={<SyncIcon />}
                onClick={showPushActivitiesToDonorPerfectDialog}
              >
                Sync to DonorPerfect
              </TablePopoverButton>
            )}
          </>
        }
        getOperatorOptions={getOperatorOptions}
        useGlobalSearch
        useFilters
        useDateRange={!hideDateRange}
        skipReset={skipReset}
        eventListeners={eventListeners}
      />
    </Grid>
  )
})
